import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { toast } from 'react-hot-toast';

// Redux
import { setOrderDataAction } from '../../redux/Actions/CreateOrderActions';
import { getServicesAndPricingApi } from '../../redux/Reducers/commonApis';

// Utils
import { amountCheckFn, numberConverter } from '../../utils/commonUtil';

// Components
import Loader from '../../components/Loader';
import NoData from '../../components/NoData';
import OrderPreferencesModule from '../../components/OrderPreferencesModule';

export default function StepServices({ onContinue }) {
  const dispatch = useDispatch();
  const tenant = import.meta.env.VITE_TENANT_NAME;
  const { orderServiceAddress, selectedServices, orderPreferences } = useSelector((s) => s.createNewOrder);
  const { preference } = useSelector((s) => s.myaccount.orderPreferences);
  const [availableServices, setAvailableServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [prefsMode, setPrefsMode] = useState('view');

  useEffect(() => {
    if (orderServiceAddress) {
      setServicesLoading(true);
      getServicesAndPricingApi(orderServiceAddress.id).then((res) => {
        setServicesLoading(false);
        if (res?.status === 200 || res?.status === 201) {
          const svcs = (res?.data || []).map((s) => ({
            id: s.id,
            service: s.name,
            unit: s.unit_type,
            client_fee: s.client_fee,
            priority_fee: s.priority_fee,
            image: s.image?.absolute_url || null,
          }));
          setAvailableServices(svcs);
          dispatch(setOrderDataAction({ availableServices: svcs }));
        } else toast.error(res?.message || 'Something went wrong');
      });
    }
  }, [orderServiceAddress]);

  // Init default selection
  useEffect(() => {
    if (!isEmpty(availableServices) && isEmpty(selectedServices)) {
      const defQty = tenant === 'pushlaundry' && availableServices[0]?.id === 13 ? 15 : 1;
      dispatch(
        setOrderDataAction({
          selectedServices: [{ service_id: availableServices[0].id, quantity: defQty }],
          orderIsRush: { value: false },
        })
      );
    }
  }, [availableServices]);

  // Preferences sync
  useEffect(() => {
    if (preference == null) dispatch(setOrderDataAction({ orderPreferences: undefined }));
    else {
      let p = { ...preference };
      if (
        String(p.pickup_delivery || '')
          .toLowerCase()
          .trim() !== 'other'
      )
        delete p.pickup_delivery_instructions;
      dispatch(setOrderDataAction({ orderPreferences: p }));
    }
  }, [preference]);

  const isSelected = (id) => selectedServices?.some((s) => s.service_id === id);
  const toggleService = (svc) => {
    if (isSelected(svc.id)) {
      dispatch(setOrderDataAction({ selectedServices: selectedServices.filter((s) => s.service_id !== svc.id) }));
    } else {
      const defQty = tenant === 'pushlaundry' && svc.id === 13 ? 15 : 1;
      dispatch(
        setOrderDataAction({
          selectedServices: [...(selectedServices || []), { service_id: svc.id, quantity: defQty }],
        })
      );
    }
  };
  const changeQty = (val, serviceId) => {
    const qty = numberConverter(val);
    if (qty === 0) {
      dispatch(setOrderDataAction({ selectedServices: selectedServices.filter((s) => s.service_id !== serviceId) }));
      return;
    }
    if (selectedServices?.some((s) => s.service_id === serviceId)) {
      dispatch(
        setOrderDataAction({
          selectedServices: selectedServices.map((s) => (s.service_id === serviceId ? { ...s, quantity: qty } : s)),
        })
      );
    } else {
      dispatch(
        setOrderDataAction({
          selectedServices: [...(selectedServices || []), { service_id: serviceId, quantity: qty }],
        })
      );
    }
  };

  const hasServices = !isEmpty(selectedServices);
  const pickupInstrCheck = () => {
    const pd = String(orderPreferences?.pickup_delivery || '').toLowerCase();
    if (pd !== 'other') return false;
    return String(orderPreferences?.pickup_delivery_instructions || '').trim().length < 5;
  };

  return (
    <div>
      {servicesLoading ? (
        <Loader text='Loading services...' classes='h-32' bg />
      ) : availableServices.length === 0 ? (
        <NoData text='No services available' classes='h-20' />
      ) : (
        <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
          {availableServices.map((svc) => {
            const sel = isSelected(svc.id);
            return (
              <div
                key={svc.id}
                onClick={() => toggleService(svc)}
                className={`flex cursor-pointer items-center gap-2 rounded-lg border-2 p-2 transition ${
                  sel ? 'border-client-300 bg-blue-50/30' : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <input
                  type='checkbox'
                  checked={sel}
                  onChange={() => {}}
                  className={`h-3.5 w-3.5 shrink-0 rounded ${sel ? 'text-client-300' : 'text-slate-200'}`}
                />
                {svc.image && <img src={svc.image} alt='' className='h-8 w-8 shrink-0 object-contain' />}
                <div className='min-w-0 flex-1'>
                  <p className='truncate text-sm font-medium text-slate-800'>{svc.service}</p>
                  <p className='text-xs text-slate-500'>
                    {amountCheckFn(svc.client_fee)} per {svc.unit}
                  </p>
                </div>
                <div className='flex flex-col items-center' onClick={(e) => e.stopPropagation()}>
                  <input
                    type='number'
                    className='h-8 w-12 rounded border border-slate-200 text-center text-sm'
                    value={selectedServices?.find((s) => s.service_id === svc.id)?.quantity?.toString() || ''}
                    onChange={(e) => changeQty(e.target.value, svc.id)}
                  />
                  <span className='text-[10px] text-slate-400'>Qty</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {!isEmpty(selectedServices) && (
        <div className='mt-3 rounded-lg border border-slate-200 bg-white p-3'>
          <div className='flex items-center justify-between'>
            <span className='text-xs font-semibold text-slate-600'>Preferences</span>
            <button
              className='text-client-300 text-xs font-semibold hover:underline'
              onClick={() => setPrefsMode(prefsMode === 'edit' ? 'view' : 'edit')}
            >
              {prefsMode === 'edit' ? 'Done' : 'Edit'}
            </button>
          </div>
          <div className='mt-2'>
            <OrderPreferencesModule
              type={prefsMode}
              preferences={orderPreferences}
              update={(obj) => dispatch(setOrderDataAction({ orderPreferences: obj }))}
              loader={false}
            />
          </div>
        </div>
      )}
      {/* Continue button */}
      <div className='mt-4 flex justify-end'>
        <button
          type='button'
          className={`rounded-full px-6 py-2 text-sm font-semibold text-white transition ${
            !hasServices || pickupInstrCheck()
              ? 'cursor-not-allowed bg-slate-300'
              : 'bg-client-300 hover:bg-client-400 shadow'
          }`}
          onClick={onContinue}
          disabled={!hasServices || pickupInstrCheck()}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
